// ============================================================
// QUOTE APPROVAL MODAL
// Select a quote to approve, creates PO automatically
// ============================================================

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, FileText, Loader2, AlertTriangle } from 'lucide-react';
import { runJobAction, LHS_API } from '../utils/lhsCore';

export default function QuoteApprovalModal({ 
  job, 
  isOpen,
  onClose, 
  onJobUpdated,
  onError,
}) {
  const [selectedQuoteId, setSelectedQuoteId] = useState(null);
  const [loading, setLoading] = useState(false);

  if (!isOpen || !job) return null;

  const quotes = job.quotes || [];
  const submittedQuotes = quotes.filter(q => q.status === 'submitted');

  const handleApprove = async () => {
    if (!selectedQuoteId) return;

    setLoading(true);
    try {
      const updatedJob = await runJobAction(
        LHS_API.approveQuote(job.id),
        { quote_id: selectedQuoteId }
      );

      if (onJobUpdated) {
        onJobUpdated(updatedJob);
      }

      onClose();
    } catch (error) {
      if (onError) {
        onError(error.message);
      }
    } finally {
      setLoading(false);
    }
  };

  const getContractorName = (quote) => {
    const assignment = (job.contractor_assignments || []).find(
      a => a.id === quote.assignment_id || a.contractor_id === quote.contractor_id
    );
    return assignment?.contractor_name || quote.contractor_id || 'Unknown';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: 'GBP',
    }).format(amount || 0);
  };

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          onClick={(e) => e.stopPropagation()}
          className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-purple-600 to-purple-700 p-5">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                  <FileText className="w-5 h-5 text-white" />
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-white">Approve Quote</h2>
                  <p className="text-sm text-white/70">{job.job_ref} - {job.park_name}</p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors"
              >
                <X className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-5">
            {submittedQuotes.length === 0 ? (
              <div className="text-center py-8">
                <AlertTriangle className="w-12 h-12 text-yellow-500 mx-auto mb-3" />
                <p className="text-gray-600">No quotes available for approval</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600 mb-4">
                  Select a quote to approve. This will create a Purchase Order and notify the contractor.
                </p>

                <div className="space-y-3">
                  {submittedQuotes.map((quote) => {
                    const isSelected = selectedQuoteId === quote.id;
                    return (
                      <motion.button
                        key={quote.id}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                        onClick={() => setSelectedQuoteId(quote.id)}
                        data-testid={`quote-option-${quote.id}`}
                        className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                          isSelected
                            ? 'bg-purple-50 border-purple-500 shadow-md'
                            : 'bg-white border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className={`w-6 h-6 rounded-lg flex items-center justify-center transition-colors ${
                              isSelected ? 'bg-purple-500' : 'bg-gray-100'
                            }`}>
                              {isSelected && <Check className="w-4 h-4 text-white" />}
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {getContractorName(quote)}
                              </div>
                              {quote.notes && (
                                <div className="text-xs text-gray-500 mt-0.5 truncate max-w-[200px]">
                                  {quote.notes}
                                </div>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="font-bold text-lg text-gray-900">
                              {formatCurrency(quote.amount)}
                            </div>
                          </div>
                        </div>

                        {/* Quote breakdown if available */}
                        {quote.breakdown && quote.breakdown.length > 0 && isSelected && (
                          <div className="mt-3 pt-3 border-t border-purple-200">
                            <div className="text-xs text-gray-500 mb-2">Breakdown:</div>
                            {quote.breakdown.map((item, idx) => (
                              <div key={idx} className="flex justify-between text-sm text-gray-600">
                                <span>{item.item || item.description}</span>
                                <span>{formatCurrency(item.amount)}</span>
                              </div>
                            ))}
                          </div>
                        )}
                      </motion.button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {/* Footer */}
          <div className="p-5 bg-gray-50 border-t">
            <motion.button
              whileHover={{ scale: 1.01 }}
              whileTap={{ scale: 0.99 }}
              disabled={!selectedQuoteId || loading}
              onClick={handleApprove}
              data-testid="approve-quote-btn"
              className="w-full py-3.5 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold 
                         disabled:opacity-40 disabled:cursor-not-allowed transition-all
                         flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Approving...
                </>
              ) : (
                <>
                  <Check className="w-5 h-5" />
                  Approve & Create PO
                </>
              )}
            </motion.button>

            <button
              onClick={onClose}
              className="mt-2 w-full py-2.5 rounded-xl border border-gray-300 text-gray-600 
                         hover:bg-gray-100 transition-colors font-medium"
            >
              Cancel
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
